import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import Plus from '@/images/plus.svg';
import Trash from '@/images/Trash.svg';
import theme from '@/theme';
import { Button } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { WebhookFormValues } from '../../../types';
import { FieldLabel } from '../../common';
import { InputField } from '../../common/InputField';

const HeaderFieldContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const HeaderRow = styled.div`
    display: flex;
    gap: 8px;
    align-items: start;
    margin-bottom: 8px;
`;

const StyledAddButton = styled(Button)`
    width: min-content;
    white-space: nowrap;
    & > span {
        gap: 4px;
    }
`;

const StyledValueInputField = styled(InputField)`
    width: 100%;
`;

const StyledTrashButton = styled(Button)`
    margin-top: 24px;
    width: 40px;
    height: 40px;
`;

const InputWithLabelContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
`;

const StyledNameInputWithLabelContainer = styled(InputWithLabelContainer)`
    width: 260px;
`;

export const Headers = () => {
    const {
        control,
        register,
        formState: { errors },
    } = useFormContext<WebhookFormValues>();
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'headers',
    });
    return (
        <HeaderFieldContainer>
            <FieldLabel
                label="Headers"
                optional
                tooltip="Add extra HTTP headers to the request (e.g., X-API-Key). We always include Content-Type: application/json, and we add Authorization if you choose Authentication."
            />
            {fields.map((field, index) => (
                <HeaderRow key={field.id}>
                    <StyledNameInputWithLabelContainer>
                        <FieldLabel label="Name" />
                        <InputField
                            placeholder="Name"
                            inputProps={{
                                ...register(`headers.${index}.name`, { required: 'Enter a header name' }),
                            }}
                            error={!!errors.headers?.[index]?.name}
                            helperText={errors.headers?.[index]?.name?.message}
                        />
                    </StyledNameInputWithLabelContainer>
                    <InputWithLabelContainer>
                        <FieldLabel label="Value" />
                        <StyledValueInputField
                            placeholder="Value"
                            inputProps={{
                                ...register(`headers.${index}.value`, { required: 'Enter a header value' }),
                            }}
                            error={!!errors.headers?.[index]?.value}
                            helperText={errors.headers?.[index]?.value?.message}
                        />
                    </InputWithLabelContainer>
                    <StyledTrashButton size="small" color="outlined" onClick={() => remove(index)}>
                        <MaskedIcon src={Trash} size="16px" />
                    </StyledTrashButton>
                </HeaderRow>
            ))}
            <StyledAddButton size="small" color="outlined" onClick={() => append({ name: '', value: '' })}>
                <MaskedIcon src={Plus} size="10px" color={theme.colors.secondary.main} />
                Add Header
            </StyledAddButton>
        </HeaderFieldContainer>
    );
};
